import { useState } from "react";
import { Notify, toast } from "@/components/ui/notify";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useMarkFalseAlarm } from "@/features/alarms/hooks";
import { occurrenceTitle } from "@/features/attention/occurrence-meta";
import type { AttentionOccurrence } from "@/types/attention.types";
import { getFriendlyErrorMessage } from "@/lib/api-client";

interface Props {
  occurrence: AttentionOccurrence | null;
  open: boolean;
  onClose: () => void;
}

// "marcar falso alarme" preservado na /alarms consolidada (GESTOR),
// adaptado para AttentionOccurrence (age sobre occurrence.alarm).
export function MarkFalseDialog({ occurrence, open, onClose }: Props) {
  const [justify, setJustify] = useState("");
  const markFalse = useMarkFalseAlarm();

  if (!open || !occurrence?.alarm) return null;

  async function submit() {
    if (justify.trim().length === 0) {
      toast.error("Justificativa é obrigatória.");
      return;
    }
    try {
      await markFalse.mutateAsync({
        id: occurrence!.alarm!.refId,
        data: { falseJustify: justify.trim() },
      });
      toast.success("Alarme marcado como falso.");
      setJustify("");
      onClose();
    } catch (err) {
      const msg = getFriendlyErrorMessage(err);
      toast.error("Falha ao marcar como falso.", { description: msg });
    }
  }

  return (
    // eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-noninteractive-element-interactions -- backdrop fecha via target check; Esc/foco no conteúdo do dialog
    <div
      className="fixed inset-0 z-50 flex animate-fade-in items-center justify-center bg-background/80 p-4"
      role="dialog"
      aria-modal="true"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <Card className="w-full max-w-md animate-scale-in">
        <CardContent className="space-y-3 pt-6">
          <div>
            <h2 className="text-base font-semibold">Marcar como falso</h2>
            <p className="text-xs text-muted-foreground">{occurrenceTitle(occurrence)}</p>
          </div>
          <Notify.Banner
            variant="warning"
            title="Apenas GESTOR"
            description="Esta ação envia o alarme para SHELVED (terminal)."
          />
          <div className="space-y-1.5">
            <Label htmlFor="false-justify">Justificativa (obrigatório)</Label>
            <Input
              id="false-justify"
              value={justify}
              onChange={(e) => setJustify(e.target.value)}
              placeholder="Ex: disparo espúrio por ruído de sensor."
              maxLength={500}
            />
          </div>
          <div className="flex justify-end gap-2">
            <Button variant="outline" onClick={onClose}>
              Cancelar
            </Button>
            <Button onClick={() => void submit()} disabled={markFalse.isPending}>
              {markFalse.isPending ? "Marcando…" : "Marcar falso"}
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
